import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import {
  IC_INFO_GREEN,
  IC_EXPRESS_NEW,
  IC_DELIVERY_TRUCK,
  IC_FLASH,
  IC_EXPRESS_SHEDULED,
  IC_SCHEDULE_MOON_ICON,
  IC_POM_PRODUCT_ICON,
  IC_POM_DELIVERY_BOY,
} from '@common/Assets/images';
import { AE_GLOBAL, FLAGSMITH_KEYS } from '@common/Utility/constants';
import { useSelector } from 'react-redux';
import CustomModal from '@common/Component/Modal/Modal';
import { flagsmith } from '@common/Lib/Flagsmith';
import { isRTL, useTranslation } from '@common/i18n';
import { getLangCodeFromUrl } from '../../../../i18n';
import { getAppConfigDataByLang } from '@common/Utility/common-utils';
import NoPrescriptionMounjaro from '@common/Component/NoPrescriptionMounjaro';

const DeliveryDetails = ({ detailsData, isSheduledExpress, showSheduledDeliveryMessage, isPOMEnabled, pdpPomText, productDetails }) => {
  const flashDeliveryFlag = flagsmith.getValue(FLAGSMITH_KEYS.FLASH_DELIVERY, { json: true });

  const [infoModalData, setInfoModalData] = React.useState({ visible: false, data: null });
  const appConfig = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const t = useTranslation();
  const [currentLang, setCurrentLanguage] = React.useState('');

  React.useEffect(() => {
    if(window !== 'undefined'){
      setCurrentLanguage(getLangCodeFromUrl());
    }
  }, []);

  const openModal = () => {
    if (detailsData?.isFlash && flashDeliveryFlag?.enabled) {
      setInfoModalData({
        visible: true,
        data: {
          title: t('ProductDetail.flashDelivery'),
          description: flashDeliveryFlag?.pdp?.deliveryToolTip,
        },
      });
    }
    else if (detailsData?.express === true) {
      const popupContent = getAppConfigDataByLang(appConfig, 'express_delivery_content', currentLang );
      setInfoModalData({
        visible: true,
        data: {
          title: t('ProductDetail.expressdelivery'),
          description: popupContent,
        },
      });
    } else {
      const popupContent = getAppConfigDataByLang(appConfig, 'standard_delivery_content', currentLang );
      setInfoModalData({
        visible: true,
        data: {
          title: t('ProductDetail.standarddelivery'),
          description: popupContent,
        },
      });
    }
  };

  const renderDeliveryImage = () => {
    if (isPOMEnabled && detailsData?.itemLabels?.includes(AE_GLOBAL?.RX)) {
      return (
        <div className="icPomProduct">
          <Image src={IC_POM_DELIVERY_BOY.src} layout="fill" alt={detailsData?.name} />
        </div>
      );
    }

    if (detailsData?.isScheduled && isSheduledExpress) {
      return (
        <div className="icExpressSheduled">
          <Image src={IC_EXPRESS_SHEDULED.src} layout="fill" alt={detailsData?.name} />
        </div>
      );
    }

    if (flashDeliveryFlag?.enabled && detailsData?.isFlash === true) {
      return (
        <div className="icFlash">
          <Image src={IC_FLASH.src} layout="fill" alt={detailsData?.name} />
        </div>
      );
    }

    if (detailsData?.express === true) {
      return (
        <div className="icExpress">
          <Image src={IC_EXPRESS_NEW.src} layout="fill" alt={detailsData?.name} />
        </div>
      );
    }

    return (
      <div className="ic-delivery">
        <Image src={IC_DELIVERY_TRUCK.src} width="26px" height="15px" alt={detailsData?.name} />
      </div>
    );
  };

  return (
    <>
      {isPOMEnabled && pdpPomText && detailsData?.itemLabels?.includes(AE_GLOBAL?.RX) && (
        <div className="pom-wrapper">
          <div className='rx-container'>
            <div className="pom-img">
              <Image src={IC_POM_PRODUCT_ICON.src} alt="pom icon img" width={24} height={24} />
            </div>
            <div
              className="pom-text"
              dangerouslySetInnerHTML={{ __html: pdpPomText }}
            ></div>
          </div>
          {productDetails?.isShowConsultation && 
          <NoPrescriptionMounjaro 
            shouldShowButtonOutline 
            sku={[productDetails?.sku]} 
          />}
        </div>
      )}
      <div className={`delivery-detail ${flashDeliveryFlag?.enabled && detailsData?.isFlash && 'delivery-flash'}`}>
        <div className='delivery-image-and-text'>
          {renderDeliveryImage()}
          {detailsData?.deliverByV2 && detailsData?.deliveryTextV2 && (
            <div
              className="delivery-time"
              dangerouslySetInnerHTML={{__html: detailsData?.deliveryTextV2.replace('{}',`<span class="duration ${flashDeliveryFlag?.enabled && detailsData?.isFlash ? 'flash-duration' : ''}">${detailsData?.deliverByV2}</span>`)}}
            />
          )}
        </div>
        <div className="info" onClick={openModal} data-testid="openModal">
          <Image src={IC_INFO_GREEN.src} width="20px" height="20px" alt="" />
        </div>
      </div>
      {detailsData?.isScheduled && detailsData?.scheduledDeliveryMessage && showSheduledDeliveryMessage && (
          <div className="disclaimer-scheduled-wrapper">
            <div className="diclaimer-img">
              <Image src={IC_SCHEDULE_MOON_ICON.src} alt="schedule moon icon img" width={15} height={22} />
            </div>
            <div
              className="disclaimer-scheduled-text"
              dangerouslySetInnerHTML={{ __html: detailsData?.scheduledDeliveryMessage }}
            ></div>
          </div>
        )}
      {infoModalData?.visible === true && infoModalData?.data && (
        <CustomModal
          titleLeftAligned={currentLang && !isRTL(currentLang) }
          titleRightAligned={currentLang && isRTL(currentLang) }
          footerBtnFullWidth={true}
          modalTitle={infoModalData?.data?.title}
          size="md"
          closeButton
          crossPressed={() => {
            setInfoModalData({ visible: false, data: null });
          }}
        >
          {infoModalData?.data?.description}
          {infoModalData?.data?.cta && infoModalData?.data?.cta !== '' && (
            <a href={infoModalData?.data?.cta}>{t('ProductDetail.readmore')}</a>
          )}
        </CustomModal>
      )}
    </>
  );
};

DeliveryDetails.propTypes = {
  detailsData: PropTypes.object.isRequired,
  productDetails: PropTypes.object.isRequired, 
  isSheduledExpress: PropTypes.bool.isRequired,
  showSheduledDeliveryMessage: PropTypes.bool.isRequired,
  isPOMEnabled: PropTypes.bool.isRequired,
  pdpPomText: PropTypes.string.isRequired,
};
DeliveryDetails.defaultProps = {
  detailsData: {},
  productDetails: {}, 
  isSheduledExpress: false,
  showSheduledDeliveryMessage: false,
  isPOMEnabled: false,
  pdpPomText: '',
};

export default DeliveryDetails;
